import { useMemo } from 'react'
import { toast } from 'react-toastify'
import to from 'await-to-js'
import { PiCaretDown, PiCheck } from 'react-icons/pi'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/BaseDropdown/dropdown-base'
import { useModalDepth } from '../UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from '../../styles/z-index'
import {
  ds2026DropdownContentClass,
  ds2026DropdownItemClass,
} from '../FbUI/ds2026DropdownMenu'
import { cn } from '../UI/cn'
import { SampleStatusBadge } from './SampleStatusBadge'
import {
  MANUAL_FULFILLMENT_STATUSES,
  type ManualFulfillmentStatus,
  type SampleRequestStatus,
} from '../../models/samples'
import { useUpdateSampleFulfillment } from '../../services/samples'

/** Labels for the manually-settable fulfillment statuses. */
export const MANUAL_STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted',
  sent: 'Sent',
  delivered: 'Delivered',
}

interface EditableSampleStatusCellProps {
  sampleId: number
  status: SampleRequestStatus
  statusDisplay: string
  /** When false, renders a plain (non-editable) badge. */
  editable: boolean
}

/**
 * Status cell for the sample table (FIR-7033).
 *
 * For standard/admin users on a fulfillable sample, renders the status badge as
 * a dropdown trigger that patches the status to submitted/sent/delivered via the
 * fulfillment endpoint. The mutation's invalidation registry refreshes the
 * table, so no manual refetch is needed. Otherwise renders a read-only badge.
 */
export function EditableSampleStatusCell({
  sampleId,
  status,
  statusDisplay,
  editable,
}: EditableSampleStatusCellProps) {
  const modalDepth = useModalDepth()
  const zIndex = useMemo(() => getDropdownZIndex(modalDepth), [modalDepth])
  const mutation = useUpdateSampleFulfillment()

  const badge = (
    <SampleStatusBadge
      status={status}
      label={statusDisplay}
      style={{ marginBottom: 2 }}
    />
  )

  if (!editable) {
    return badge
  }

  const handleSelect = async (next: ManualFulfillmentStatus) => {
    if (next === status) return
    const [err] = await to(
      mutation.mutateAsync({ id: sampleId, data: { status: next } })
    )
    if (err) {
      toast.error('Failed to update status')
    } else {
      toast.success(`Status updated to ${MANUAL_STATUS_LABELS[next] ?? next}`)
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={mutation.isPending}>
        <button
          type="button"
          aria-label="Edit status"
          className="group inline-flex items-center gap-1 rounded focus:outline-none disabled:opacity-60"
          onClick={(e) => e.stopPropagation()}
        >
          {badge}
          <PiCaretDown
            size={12}
            className="text-fg-tertiary group-hover:text-fg-secondary"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        style={{ minWidth: 160, zIndex }}
        className={ds2026DropdownContentClass}
      >
        {MANUAL_FULFILLMENT_STATUSES.map((option) => (
          <DropdownMenuItem
            key={option}
            className={cn(ds2026DropdownItemClass, 'justify-between gap-2')}
            onSelect={() => void handleSelect(option)}
          >
            <SampleStatusBadge
              status={option}
              label={MANUAL_STATUS_LABELS[option] ?? option}
            />
            {option === status && (
              <PiCheck size={16} className="text-fg-secondary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
